/**
 * EmailClientPage · v2.7.45 (2026-02-22) — Corporate Webmail
 * ═════════════════════════════════════════════════════════════════════
 * Square-style 3-column dispatch webmail client.
 *
 *   ┌────────────────┬─────────────────────┬─────────────────────────┐
 *   │  Accounts +    │  Message list       │  Reading pane           │
 *   │  Folder tree   │  (current folder)   │  / Composer modal       │
 *   └────────────────┴─────────────────────┴─────────────────────────┘
 *
 * Strict per-account isolation: switching activeAccountID resets folders,
 * message list, reading pane, and the composer's From dropdown.
 *
 * Connection health badges:
 *   • 🟢 green dot   — last test handshake OK
 *   • 🔴 red dot     — IMAP/SMTP error (renders alongside `last_health.error`)
 *   • ⚪ neutral dot — never tested (run from the menu)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import adminApi from "@/lib/adminApi";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Mail, Inbox, Send, FileText, FileText as Drafts, AlertTriangle as Spam,
  Trash2, Archive, Folder, ChevronDown, ChevronRight, Plus,
  RefreshCw, X, Reply, Forward, MailPlus, Loader2, ShieldAlert, Check,
  ArrowLeft, LayoutDashboard, Settings, ChevronUp,
  Flag, Zap, AlertOctagon, Filter, Layout, Rows3, SquareSplitVertical,
  Image as ImageIcon, Paperclip, History,
} from "lucide-react";
import EmailSettingsPanel from "@/components/admin/EmailSettingsPanel";
import ActiveEmailAccountsTable from "@/components/admin/ActiveEmailAccountsTable";
import AllInboxesSnapshot from "@/components/admin/AllInboxesSnapshot";
import TranslationToggle from "@/components/admin/TranslationToggle";
import EmailRulesPanel from "@/components/admin/EmailRulesPanel";
import useEmailViewMode from "@/hooks/useEmailViewMode";
import { BISON_CORPORATE_EMAIL_LOGO, TAXI_COMMANDER_LOGO } from "@/lib/brandAssets";

// Standardised folder ordering — `\Flag` flag-based mapping (IONOS uses
// \HasNoChildren \Drafts etc to tag standard folders).
const FOLDER_ICONS = {
  INBOX:        Inbox,
  Drafts:       Drafts,
  "Sent Items": Send,
  Sent:         Send,
  Spam:         Spam,
  Junk:         Spam,
  Trash:        Trash2,
  Archive:      Archive,
};
const STANDARD_ORDER = ["INBOX", "Drafts", "Sent Items", "Sent", "Spam", "Junk", "Trash", "Archive"];

function classifyFolder(f) {
  const flags = f.flags || "";
  if (flags.includes("\\Drafts"))  return "Drafts";
  if (flags.includes("\\Sent"))    return "Sent";
  if (flags.includes("\\Junk"))    return "Spam";
  if (flags.includes("\\Trash"))   return "Trash";
  if (flags.includes("\\Archive")) return "Archive";
  if (f.name === "INBOX")          return "INBOX";
  return "Custom";
}

export default function EmailClientPage() {
  useTheme(); // ensure CSS vars resolve correctly
  const location = useLocation();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [adminRole, setAdminRole] = useState(null);
  const [activeAccountId, setActiveAccountId] = useState(null);
  const [expandedAccounts, setExpandedAccounts] = useState({}); // {accountId: true}
  const [foldersByAccount, setFoldersByAccount] = useState({}); // {accountId: [{name, flags}]}
  const [activeFolder, setActiveFolder] = useState("INBOX");
  const [messages, setMessages] = useState({ folder: "INBOX", total: 0, items: [] });
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [selectedUid, setSelectedUid] = useState(null);
  const [reading, setReading] = useState(null);
  const [readingLoading, setReadingLoading] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerSeed, setComposerSeed] = useState(null);
  const [showFolders, setShowFolders] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // v2.7.45+E7 · Dashboard reorganization (spec §1). Persisted to
  // localStorage so the user's collapse preference survives reloads.
  const [showDashboard, setShowDashboard] = useState(() => {
    try { return localStorage.getItem("rtg-email-dashboard-open") !== "0"; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("rtg-email-dashboard-open", showDashboard ? "1" : "0"); }
    catch { /* private mode */ }
  }, [showDashboard]);
  // v2.7.45+E3 · When viewing an alias mailbox, default to FILTERED
  // (only show messages addressed to the alias). Toggle exposes the
  // parent's full INBOX for occasions when an admin needs the bigger
  // picture (cross-alias context, debugging routing, etc.).
  const [showAllInbox, setShowAllInbox] = useState(false);
  // v2.7.45+E12 · multi-select for batch actions + folder role_map for
  // delete/archive/spam destination resolution + rules drawer toggle.
  const [selectedUids, setSelectedUids] = useState(() => new Set());
  const [roleMapByAccount, setRoleMapByAccount] = useState({}); // {accountId: {trash: "Trash", ...}}
  const [actionRunning, setActionRunning] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  // v2.7.45+E13 · Email view mode (three-pane | list | compact)
  const { mode: viewMode, setMode: setViewMode } = useEmailViewMode();
  const [listModalOpen, setListModalOpen] = useState(false);  // List mode opens reading pane as modal

  // ── Load accounts on mount ──
  const reloadAccounts = useCallback(async () => {
    try {
      const r = await adminApi.get("/admin/email/accounts");
      const items = r.data?.items || [];
      setAccounts(items);
      const def = items.find((a) => a.is_default) || items[0];
      if (def && !activeAccountId) {
        setActiveAccountId(def.id);
        setExpandedAccounts((e) => ({ ...e, [def.id]: true }));
      }
    } catch (e) {
      toast.error("Failed to load mail accounts");
    }
  }, [activeAccountId]);
  useEffect(() => { reloadAccounts(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  // v2.7.45+E7 · admin role drives Global Disclaimer write-access
  useEffect(() => {
    let cancelled = false;
    adminApi.get("/admin/me/roles")
      .then((r) => { if (!cancelled) setAdminRole(r.data?.primary_role || r.data?.role || null); })
      .catch(() => { /* silent — drawer just falls back to read-only */ });
    return () => { cancelled = true; };
  }, []);

  // v2.7.45+E4 · React to URL query changes — sidebar links use these:
  //   /admin/email?account=<id>   → switch to that mailbox
  //   /admin/email?settings=1     → open the Mail Account Settings drawer
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const wantsSettings = params.get("settings") === "1";
    if (wantsSettings) setSettingsOpen(true);
    const wantsAccount = params.get("account");
    if (wantsAccount && accounts.some((a) => a.id === wantsAccount)) {
      setActiveAccountId(wantsAccount);
      setActiveFolder("INBOX");
      setSelectedUid(null);
      setReading(null);
      setShowAllInbox(false);
      setExpandedAccounts((e) => ({ ...e, [wantsAccount]: true }));
    }
  }, [location.search, accounts]);

  const closeSettings = useCallback(() => {
    setSettingsOpen(false);
    const params = new URLSearchParams(location.search);
    if (params.get("settings") === "1") {
      params.delete("settings");
      const tail = params.toString();
      navigate(`/admin/email${tail ? `?${tail}` : ""}`, { replace: true });
    }
  }, [location.search, navigate]);

  // ── When an account is clicked: expand its folder tree AND switch
  //   the active context to its INBOX. Folder list is cached after first
  //   fetch so the second click on an already-expanded account just
  //   re-arms the activeAccount without a refetch.
  const expandAccount = useCallback(async (accountId) => {
    setExpandedAccounts((e) => ({ ...e, [accountId]: !e[accountId] }));
    // Always switch active context so the banner + filtered view reflect
    // the clicked mailbox immediately — even if folders are still loading.
    setActiveAccountId(accountId);
    setActiveFolder("INBOX");
    setSelectedUid(null);
    setReading(null);
    setShowAllInbox(false);
    if (foldersByAccount[accountId]) return;
    try {
      const r = await adminApi.get(`/admin/email/accounts/${accountId}/folders`);
      setFoldersByAccount((f) => ({ ...f, [accountId]: r.data?.items || [] }));
      // v2.7.45+E12 · cache role_map so action buttons can target the right
      // IMAP folder (Trash/Spam/Archive) without hardcoding labels.
      setRoleMapByAccount((m) => ({ ...m, [accountId]: r.data?.role_map || {} }));
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Connection failed";
      toast.error(`Could not load folders: ${msg}`);
      setFoldersByAccount((f) => ({ ...f, [accountId]: [] }));
    }
  }, [foldersByAccount]);

  // ── Run connection test (and refresh local health) ──
  const runHealthTest = useCallback(async (accountId) => {
    try {
      const r = await adminApi.post(`/admin/email/accounts/${accountId}/test`);
      const ok = r.data?.ok;
      toast[ok ? "success" : "error"](ok ? "Connection OK" : `Connection failed: ${r.data?.error || "unknown"}`);
      reloadAccounts();
    } catch (e) {
      toast.error("Test failed");
    }
  }, [reloadAccounts]);

  // ── Switch active account (resets isolation context) ──
  const switchAccount = useCallback((accountId, folder = "INBOX") => {
    setActiveAccountId(accountId);
    setActiveFolder(folder);
    setMessages({ folder, total: 0, items: [] });
    setSelectedUid(null);
    setSelectedUids(new Set());
    setReading(null);
    setShowAllInbox(false);  // re-arm alias filter on every account switch
    setExpandedAccounts((e) => ({ ...e, [accountId]: true }));
  }, []);

  // ── v2.7.45+E12 · Selected-message batch actions ──
  // All four entrypoints share the same shape: build a UIDs array, hit
  // the move/flag endpoint, then refetch the list so removed messages
  // disappear from the UI.
  const refetchMessages = useCallback(() => {
    if (!activeAccountId) return;
    const showAllParam = showAllInbox ? "&show_all=true" : "";
    return adminApi
      .get(`/admin/email/accounts/${activeAccountId}/messages?folder=${encodeURIComponent(activeFolder)}&limit=50${showAllParam}`)
      .then((r) => setMessages(r.data || { folder: activeFolder, total: 0, items: [] }))
      .catch(() => { /* surfaced elsewhere */ });
  }, [activeAccountId, activeFolder, showAllInbox]);

  const runMoveAction = useCallback(async (destRole) => {
    const uids = Array.from(selectedUids);
    if (!uids.length || !activeAccountId) { toast.info("Select at least one message"); return; }
    setActionRunning(true);
    try {
      const r = await adminApi.post(
        `/admin/email/accounts/${activeAccountId}/messages/move`,
        { folder: activeFolder, uids, dest_role: destRole },
      );
      const n = r.data?.moved || 0;
      const dest = r.data?.dest_folder || destRole;
      toast.success(`Moved ${n} message${n === 1 ? "" : "s"} to ${dest}`);
      setSelectedUids(new Set());
      if (selectedUid && uids.includes(selectedUid)) {
        setSelectedUid(null);
        setReading(null);
      }
      await refetchMessages();
    } catch (e) {
      toast.error(`Move failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setActionRunning(false);
    }
  }, [activeAccountId, activeFolder, selectedUid, selectedUids, refetchMessages]);

  const runFlagAction = useCallback(async (flagName, on) => {
    const uids = Array.from(selectedUids);
    if (!uids.length || !activeAccountId) { toast.info("Select at least one message"); return; }
    setActionRunning(true);
    try {
      const r = await adminApi.post(
        `/admin/email/accounts/${activeAccountId}/messages/flag`,
        { folder: activeFolder, uids, flag: flagName, on },
      );
      const n = r.data?.updated || 0;
      const label = flagName === "important" ? "Important" : flagName.charAt(0).toUpperCase() + flagName.slice(1);
      toast.success(`${on ? "Set" : "Cleared"} ${label} on ${n} message${n === 1 ? "" : "s"}`);
      if (flagName === "important" && on && (r.data?.escalations || []).length) {
        const ev = r.data.escalations[0];
        toast.message(`Supervisors notified · ${ev.fanned_out} in-app · ${ev.blast_sent} email blast(s)`, { duration: 5000 });
      }
      setSelectedUids(new Set());
      await refetchMessages();
    } catch (e) {
      toast.error(`Flag failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setActionRunning(false);
    }
  }, [activeAccountId, activeFolder, selectedUids, refetchMessages]);

  // Single-message Important toggle from the reading pane
  const toggleReadingImportant = useCallback(async () => {
    if (!selectedUid || !activeAccountId) return;
    setActionRunning(true);
    try {
      const r = await adminApi.post(
        `/admin/email/accounts/${activeAccountId}/messages/flag`,
        { folder: activeFolder, uids: [selectedUid], flag: "important", on: true },
      );
      const escs = r.data?.escalations || [];
      toast.success(`Important · supervisors notified${escs[0] ? ` (${escs[0].fanned_out} recipient${escs[0].fanned_out === 1 ? "" : "s"})` : ""}`);
    } catch (e) {
      toast.error(`Failed to escalate: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setActionRunning(false);
    }
  }, [activeAccountId, activeFolder, selectedUid]);

  // ── Fetch messages whenever active account/folder/show-all changes ──
  useEffect(() => {
    if (!activeAccountId) return;
    setLoadingMessages(true);
    setSelectedUid(null);
    setSelectedUids(new Set());
    setReading(null);
    const showAllParam = showAllInbox ? "&show_all=true" : "";
    adminApi.get(`/admin/email/accounts/${activeAccountId}/messages?folder=${encodeURIComponent(activeFolder)}&limit=50${showAllParam}`)
      .then((r) => setMessages(r.data || { folder: activeFolder, total: 0, items: [] }))
      .catch((e) => {
        const msg = e?.response?.data?.detail || "Connection error";
        toast.error(`Could not load ${activeFolder}: ${msg}`);
        setMessages({ folder: activeFolder, total: 0, items: [], error: msg });
      })
      .finally(() => setLoadingMessages(false));
  }, [activeAccountId, activeFolder, showAllInbox]);

  // ── Fetch full message body on row click ──
  useEffect(() => {
    if (!activeAccountId || !selectedUid) return;
    setReadingLoading(true);
    adminApi.get(`/admin/email/accounts/${activeAccountId}/messages/${selectedUid}?folder=${encodeURIComponent(activeFolder)}`)
      .then((r) => setReading(r.data || null))
      .catch((e) => toast.error(`Open failed: ${e?.response?.data?.detail || e.message}`))
      .finally(() => setReadingLoading(false));
  }, [activeAccountId, selectedUid, activeFolder]);

  const activeAccount = useMemo(() => accounts.find((a) => a.id === activeAccountId), [accounts, activeAccountId]);

  return (
    <div className="min-h-screen bg-white dark:bg-[#0c0d10] text-slate-900 dark:text-zinc-100" data-testid="email-client-root">
      {/* v2.7.48+E23 · Dual-logo header (Taxi Commander + Bison Email)
          Both marks centered side-by-side at exactly 50 % of the
          previous single-Bison header height. Symmetrical baseline,
          no scale transforms, no inner padding. */}
      <div
        className="bg-slate-50 dark:bg-zinc-950 flex items-center justify-center gap-4 sm:gap-6 md:gap-8 border-b border-slate-200 dark:border-zinc-800 overflow-hidden"
        style={{ padding: 0, margin: 0 }}
        data-testid="email-bison-brand-top"
      >
        <img
          src={TAXI_COMMANDER_LOGO}
          alt="Taxi Commander · Admin Console Software"
          className="block h-16 sm:h-20 md:h-[100px] w-auto object-contain"
          style={{ maxWidth: "50%", margin: 0, padding: 0, display: "block" }}
          data-testid="email-taxi-commander-brand-top"
        />
        <img
          src={BISON_CORPORATE_EMAIL_LOGO}
          alt="Bison Software · Corporate Email System"
          className="block h-16 sm:h-20 md:h-[100px] w-auto object-contain"
          style={{ maxWidth: "50%", margin: 0, padding: 0, display: "block" }}
        />
      </div>
      {/* Page header */}
      <div className="border-b border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 px-4 py-3 flex items-center justify-between gap-3 sticky top-0 z-20">
        <div className="flex items-center gap-3 min-w-0">
          {/* v2.7.45 · Persistent escape hatch back to the main Admin Console.
              The email client is a full-screen route (no sidebar) so this is
              the only way out short of the browser back button. */}
          <Button asChild variant="outline" size="sm" className="bg-white border-slate-300 text-slate-800 hover:bg-slate-100 hover:text-slate-900 dark:bg-zinc-950 dark:border-zinc-700 dark:text-zinc-300 dark:hover:text-amber-400 h-9 text-xs gap-1.5 flex-shrink-0" data-testid="email-back-to-console">
            <Link to="/admin">
              <ArrowLeft className="h-3.5 w-3.5"/>
              <span className="hidden sm:inline">Admin Console</span>
            </Link>
          </Button>
          <button
            type="button"
            onClick={() => setShowFolders((s) => !s)}
            className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-md border border-slate-300 dark:border-zinc-700 text-slate-700 dark:text-zinc-200"
            data-testid="email-toggle-folders"
            aria-label="Toggle folder panel"
          >
            {showFolders ? <X className="h-4 w-4"/> : <Mail className="h-4 w-4"/>}
          </button>
          <div className="h-6 w-px bg-slate-300 dark:bg-zinc-700 hidden sm:block"/>
          <Mail className="h-5 w-5 text-sky-500 flex-shrink-0"/>
          <div className="font-bold tracking-wide text-lg truncate">CORPORATE EMAIL</div>
          {activeAccount && (
            <div className="hidden md:block text-xs text-slate-500 dark:text-zinc-500 truncate">
              · {activeAccount.email}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button asChild variant="ghost" size="sm" className="hidden md:inline-flex gap-1.5 text-slate-600 dark:text-zinc-400 hover:text-amber-600 dark:hover:text-amber-400 h-9 text-xs" data-testid="email-jump-console">
            <Link to="/admin">
              <LayoutDashboard className="h-3.5 w-3.5"/>Dashboard
            </Link>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRulesOpen(true)}
            className="gap-1.5 h-9 text-xs"
            title="Email rules engine"
            data-testid="email-rules-btn"
          >
            <Filter className="h-3.5 w-3.5"/>Rules
          </Button>
          <ViewModeSwitcher mode={viewMode} onChange={setViewMode}/>
          <Button
            data-testid="email-compose-btn"
            className="bg-sky-600 hover:bg-sky-500 text-white font-bold gap-2 h-9"
            onClick={() => { setComposerSeed(null); setComposerOpen(true); }}
          >
            <MailPlus className="h-4 w-4"/>Compose
          </Button>
        </div>
      </div>

      {/* v2.7.45+E7 · Dashboard reorganization (spec §1)
          Top: Active Email Accounts table → All Inboxes Snapshot → Mailboxes (3-col below) → Mail Account Settings (drawer).
          User can collapse this strip to recover full vertical real estate for the email client. */}
      <DashboardStrip
        showDashboard={showDashboard}
        onToggle={() => setShowDashboard((s) => !s)}
        onOpenMailbox={(id) => navigate(`/admin/email?account=${encodeURIComponent(id)}`)}
        onOpenSettings={() => setSettingsOpen(true)}
      />

      {/* v2.7.45+E13 · Mode-aware layout
         • three-pane: classic 3 columns side-by-side (accounts | list | reading)
         • list: 2 columns; reading pane lives inside a modal on click
         • compact: 2 columns with right side stacked vertically (list on top,
                    reading pane on bottom). Compressed row padding.       */}
      <div
        className={`flex ${viewMode === "list" ? "email-mode-list" : viewMode === "compact" ? "email-mode-compact" : "email-mode-three-pane"}`}
        style={{ minHeight: "calc(100vh - 60px)" }}
        data-testid={`email-layout-${viewMode}`}
      >
        {/* ───── Column 1 · Accounts + Folder tree ───── */}
        <aside
          className={`${showFolders ? "block" : "hidden"} lg:block w-72 flex-shrink-0 border-r border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-950 overflow-y-auto`}
          data-testid="email-account-rail"
          style={{ maxHeight: "calc(100vh - 60px)" }}
        >
          <div className="p-3 border-b border-slate-200 dark:border-zinc-800 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400">Accounts</div>
            <Button
              size="sm" variant="ghost"
              className="h-7 w-7 p-0 text-sky-600 dark:text-sky-400"
              data-testid="email-add-account-btn"
              onClick={() => setSettingsOpen(true)}
              title="Add Email Account"
            >
              <Plus className="h-4 w-4"/>
            </Button>
          </div>
          {accounts.length === 0 && (
            <div className="p-4 text-sm text-slate-500 dark:text-zinc-500">No mail accounts yet.</div>
          )}
          {accounts.map((acct) => (
            <AccountAccordion
              key={acct.id}
              account={acct}
              expanded={!!expandedAccounts[acct.id]}
              isActive={acct.id === activeAccountId}
              activeFolder={acct.id === activeAccountId ? activeFolder : null}
              folders={foldersByAccount[acct.id] || []}
              onToggle={() => expandAccount(acct.id)}
              onSelectFolder={(folder) => switchAccount(acct.id, folder)}
              onTest={() => runHealthTest(acct.id)}
            />
          ))}
          <div className="p-3 border-t border-slate-200 dark:border-zinc-800 mt-2">
            <Button
              variant="outline" size="sm" className="w-full text-xs gap-2"
              data-testid="email-settings-btn"
              onClick={() => setSettingsOpen(true)}
            >
              <Settings className="h-3.5 w-3.5"/>
              Mail Account Settings
            </Button>
          </div>
        </aside>

        {/* ───── Right side: Message list + Reading pane ─────
             Three-pane: side-by-side. Compact: stacked vertically.
             List: full-width list; reading lives in a modal triggered on row click. */}
        <div className={`flex-1 min-w-0 flex ${viewMode === "compact" ? "flex-col" : ""}`}>
        {/* ───── Column 2 · Message list ───── */}
        <section
          className={`${viewMode === "list"
              ? "flex-1 min-w-0"
              : viewMode === "compact"
                ? "flex-shrink-0 border-b border-slate-200 dark:border-zinc-800"
                : "w-full max-w-xl flex-shrink-0 border-r border-slate-200 dark:border-zinc-800"} overflow-y-auto`}
          data-testid="email-list-col"
          style={viewMode === "compact" ? { maxHeight: "50vh" } : { maxHeight: "calc(100vh - 60px)" }}
        >
          <div className="border-b border-slate-200 dark:border-zinc-800 sticky top-0 bg-white dark:bg-[#0c0d10] z-10">
            <div className="p-3 flex items-center justify-between">
              <div className="text-sm font-bold tracking-wide truncate">
                {activeFolder}
                <span className="ml-2 text-xs text-slate-500 dark:text-zinc-500">{messages.total ? `(${messages.total})` : ""}</span>
              </div>
              <Button
                variant="ghost" size="sm" className="h-7 w-7 p-0"
                onClick={() => setMessages((m) => ({ ...m })) /* trigger re-fetch */ || setActiveFolder((f) => f)}
                data-testid="email-refresh-list"
                title="Refresh"
              >
                <RefreshCw className={`h-4 w-4 ${loadingMessages ? "animate-spin" : ""}`}/>
              </Button>
            </div>
            {/* v2.7.45+E3 · Alias virtual-inbox banner — explains the filter
                so admins don't think the list is broken when an alias
                hasn't received any direct mail yet, AND offers a one-click
                escape to see the parent's full INBOX. */}
            {activeAccount?.is_alias && activeFolder.toUpperCase() === "INBOX" && (
              <div className={`px-3 py-2 text-xs border-t ${showAllInbox
                ? "bg-slate-50 dark:bg-zinc-900 border-slate-200 dark:border-zinc-800 text-slate-700 dark:text-zinc-300"
                : "bg-amber-50 dark:bg-amber-900/15 border-amber-200 dark:border-amber-900/40 text-amber-800 dark:text-amber-200"
              }`} data-testid="email-alias-banner">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0 leading-tight">
                    {showAllInbox ? (
                      <>
                        <b>Parent inbox view</b> · all mail in
                        <span className="font-mono"> {activeAccount.alias_of_email}</span>
                      </>
                    ) : (
                      <>
                        <b>Virtual inbox</b> · only mail addressed to
                        <span className="font-mono"> {activeAccount.email}</span>
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowAllInbox((s) => !s)}
                    className="text-[10px] font-bold uppercase tracking-widest underline hover:no-underline whitespace-nowrap flex-shrink-0"
                    data-testid="email-toggle-show-all"
                  >
                    {showAllInbox ? "Filter to alias" : "Show all"}
                  </button>
                </div>
              </div>
            )}
            {/* v2.7.45+E12 · Selected-message action toolbar.
                Visible whenever ≥1 row is checked. Buttons issue
                IMAP move/flag operations and the Important button
                fires the supervisor escalation pipeline. */}
            {selectedUids.size > 0 && (
              <ActionToolbar
                count={selectedUids.size}
                running={actionRunning}
                onClear={() => setSelectedUids(new Set())}
                onFlag={() => runFlagAction("flagged", true)}
                onImportant={() => runFlagAction("important", true)}
                onArchive={() => runMoveAction("archive")}
                onSpam={() => runMoveAction("spam")}
                onDelete={() => runMoveAction("trash")}
              />
            )}
          </div>
          {loadingMessages && messages.items.length === 0 && (
            <div className="p-6 text-center text-slate-500 dark:text-zinc-500">
              <Loader2 className="h-6 w-6 animate-spin inline-block mr-2"/>
              Loading {activeFolder}…
            </div>
          )}
          {!loadingMessages && messages.items.length === 0 && (
            <div className="p-6 text-center text-slate-400 dark:text-zinc-600 text-sm">
              {activeAccount?.is_alias && !showAllInbox && activeFolder.toUpperCase() === "INBOX"
                ? `No mail addressed directly to ${activeAccount.email} yet. Click "Show all" to see the parent inbox.`
                : "No messages"}
            </div>
          )}
          {messages.items.map((m) => (
            <MessageRow
              key={m.uid}
              msg={m}
              selected={m.uid === selectedUid}
              checked={selectedUids.has(m.uid)}
              onToggleCheck={() => {
                setSelectedUids((prev) => {
                  const next = new Set(prev);
                  if (next.has(m.uid)) next.delete(m.uid); else next.add(m.uid);
                  return next;
                });
              }}
              onClick={() => setSelectedUid(m.uid)}
            />
          ))}
        </section>

        {/* ───── Column 3 · Reading pane (hidden in List mode unless modal is open) ───── */}
        {viewMode !== "list" && (
        <section
          className={`flex-1 min-w-0 overflow-y-auto`}
          data-testid="email-reading-pane"
          style={viewMode === "compact" ? { maxHeight: "50vh" } : { maxHeight: "calc(100vh - 60px)" }}
        >
          {!selectedUid && (
            <div className="h-full min-h-[60vh] flex flex-col items-center justify-center text-slate-400 dark:text-zinc-600 p-8 text-center">
              <Mail className="h-12 w-12 mb-3 opacity-50"/>
              <div className="font-bold text-lg">Select a message</div>
              <div className="text-sm mt-1">Choose a thread on the left to read it here.</div>
            </div>
          )}
          {selectedUid && readingLoading && (
            <div className="p-6 text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline-block mr-2"/>Opening message…</div>
          )}
          {selectedUid && !readingLoading && reading && (
            <ReadingPane
              msg={reading}
              account={activeAccount}
              onReply={() => { setComposerSeed({ ...reading, mode: "reply" }); setComposerOpen(true); }}
              onForward={() => { setComposerSeed({ ...reading, mode: "forward" }); setComposerOpen(true); }}
              onImportant={toggleReadingImportant}
            />
          )}
        </section>
        )}
        </div>{/* end right-side wrapper (list+reading) */}
      </div>

      {/* v2.7.45+E13 · List-mode reading modal — opens when a row is
          selected in List view. Keeps the message list full-width while
          giving the user the same ReadingPane UI on demand. */}
      {viewMode === "list" && selectedUid && reading && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => { setSelectedUid(null); }}
          data-testid="email-list-mode-modal"
        >
          <div
            className="bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white dark:bg-zinc-950 border-b border-slate-200 dark:border-zinc-800 px-4 py-2 flex items-center justify-between z-10">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400">List view · reading</div>
              <button type="button" onClick={() => setSelectedUid(null)} className="text-slate-500 hover:text-slate-900 dark:hover:text-zinc-100" aria-label="Close reading pane" data-testid="email-list-modal-close">
                <X className="h-5 w-5"/>
              </button>
            </div>
            <ReadingPane
              msg={reading}
              account={activeAccount}
              onReply={() => { setComposerSeed({ ...reading, mode: "reply" }); setComposerOpen(true); }}
              onForward={() => { setComposerSeed({ ...reading, mode: "forward" }); setComposerOpen(true); }}
              onImportant={toggleReadingImportant}
            />
          </div>
        </div>
      )}

      {/* Composer modal */}
      {composerOpen && (
        <ComposerModal
          accounts={accounts}
          defaultAccount={activeAccount}
          seed={composerSeed}
          onClose={() => { setComposerOpen(false); setComposerSeed(null); }}
        />
      )}

      {/* Mail Account Settings drawer */}
      {settingsOpen && (
        <EmailSettingsPanel
          accounts={accounts}
          adminRole={adminRole}
          onClose={closeSettings}
          onChanged={() => {
            reloadAccounts();
            window.dispatchEvent(new CustomEvent("rtg-email-accounts-changed"));
          }}
        />
      )}

      {/* v2.7.45+E12 · Email Rules drawer */}
      {rulesOpen && (
        <EmailRulesPanel
          accounts={accounts}
          onClose={() => setRulesOpen(false)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DashboardStrip · v2.7.45+E7 spec §1 — collapsible top section that
// hosts the Active Accounts table + All Inboxes Snapshot above the
// existing 3-column email client.
// ─────────────────────────────────────────────────────────────────────
function DashboardStrip({ showDashboard, onToggle, onOpenMailbox, onOpenSettings }) {
  return (
    <div className="border-b border-slate-200 dark:border-zinc-800 bg-slate-100/40 dark:bg-zinc-900/30">
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-1.5 flex items-center justify-between text-[10px] uppercase tracking-widest font-bold text-slate-700 dark:text-zinc-300 hover:bg-slate-200/50 dark:hover:bg-zinc-800/50 transition-colors"
        data-testid="email-dashboard-toggle"
      >
        <span>Dashboard · Accounts &amp; Inbox Snapshot</span>
        {showDashboard ? <ChevronUp className="h-3 w-3"/> : <ChevronDown className="h-3 w-3"/>}
      </button>
      {showDashboard && (
        <div className="px-4 pb-4 pt-2 space-y-3">
          <ActiveEmailAccountsTable
            onOpenMailbox={onOpenMailbox}
            onOpenSettings={onOpenSettings}
          />
          <AllInboxesSnapshot/>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Per-account accordion w/ standard folder mapping + custom folders
// ─────────────────────────────────────────────────────────────────────
function AccountAccordion({ account, expanded, isActive, activeFolder, folders, onToggle, onSelectFolder, onTest }) {
  const health = account.last_health || {};
  const healthDot =
    health.ok === true  ? "bg-emerald-500" :
    health.ok === false ? "bg-rose-500"    :
    "bg-slate-300 dark:bg-zinc-700";
  const healthTitle =
    health.ok === true  ? `Connection OK · ${health.checked_at || ""}` :
    health.ok === false ? `Connection error: ${health.error || "unknown"}` :
    "Never tested — click the shield to run a handshake";

  // Classify folder list
  const standardFolders = [];
  const customFolders = [];
  for (const f of folders) {
    const klass = classifyFolder(f);
    if (klass === "Custom") customFolders.push(f);
    else standardFolders.push({ ...f, _klass: klass });
  }
  // Order standard folders consistently
  standardFolders.sort((a, b) => STANDARD_ORDER.indexOf(a._klass) - STANDARD_ORDER.indexOf(b._klass));

  return (
    <div className={`border-b border-slate-200 dark:border-zinc-800 ${isActive ? "bg-sky-50 dark:bg-sky-900/10" : ""}`} data-testid={`email-account-block-${account.email}`}>
      <div className="flex items-stretch">
        {/* Main accordion toggle — div instead of button so we can nest the
            test-action button without invalid-HTML hydration warnings. */}
        <div
          role="button"
          tabIndex={0}
          onClick={onToggle}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
          className="flex-1 min-w-0 flex items-center gap-2 px-3 py-2 hover:bg-slate-100 dark:hover:bg-zinc-900 transition-colors cursor-pointer focus:outline-none focus-visible:bg-slate-100 dark:focus-visible:bg-zinc-900"
          data-testid={`email-account-toggle-${account.email}`}
        >
          {expanded ? <ChevronDown className="h-4 w-4 text-slate-500"/> : <ChevronRight className="h-4 w-4 text-slate-500"/>}
          <span className={`inline-block h-2 w-2 rounded-full ${healthDot}`} title={healthTitle} data-testid={`email-health-dot-${account.email}`}/>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <div className="text-sm font-bold truncate text-slate-900 dark:text-zinc-100">{account.label || account.email}</div>
              {account.is_alias && (
                <span className="text-[8px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" data-testid={`email-alias-chip-${account.email}`} title={`Inherits IMAP/SMTP from ${account.alias_of_email}`}>
                  Alias
                </span>
              )}
            </div>
            <div className="text-[10px] text-slate-500 dark:text-zinc-500 truncate">{account.email}</div>
            {account.is_alias && account.alias_of_email && (
              <div className="text-[9px] text-amber-600 dark:text-amber-400 truncate italic" data-testid={`email-alias-parent-${account.email}`}>
                ↳ via {account.alias_of_email}
              </div>
            )}
          </div>
          {health.ok === false && <ShieldAlert className="h-4 w-4 text-rose-500 flex-shrink-0" title={health.error}/>}
        </div>
        {/* Test action — sibling button, no nesting. */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onTest(); }}
          className="px-2 text-sky-600 dark:text-sky-400 hover:bg-sky-100 dark:hover:bg-sky-900/30 transition-colors border-l border-slate-200 dark:border-zinc-800 flex items-center"
          data-testid={`email-test-${account.email}`}
          title="Run connection test"
          aria-label={`Test connection to ${account.email}`}
        >
          <Check className="h-3.5 w-3.5"/>
        </button>
      </div>
      {expanded && (
        <div className="pb-1">
          {standardFolders.length === 0 && (
            <div className="px-6 py-2 text-[11px] text-slate-500 dark:text-zinc-500">Loading folders…</div>
          )}
          {standardFolders.map((f) => {
            const Icon = FOLDER_ICONS[f._klass] || FOLDER_ICONS[f.name] || Folder;
            return (
              <FolderRow
                key={f.name}
                folder={f.name}
                label={f._klass === "Sent" ? "Sent Items" : (f._klass === "INBOX" ? "Inbox" : f._klass)}
                Icon={Icon}
                active={isActive && activeFolder === f.name}
                onClick={() => onSelectFolder(f.name)}
              />
            );
          })}
          {customFolders.length > 0 && (
            <div className="mt-1 pt-1 border-t border-slate-200 dark:border-zinc-800">
              <div className="px-6 py-1 text-[9px] uppercase tracking-widest font-bold text-slate-500 dark:text-zinc-500">My Folders</div>
              {customFolders.map((f) => (
                <FolderRow
                  key={f.name}
                  folder={f.name}
                  label={f.name}
                  Icon={Folder}
                  active={isActive && activeFolder === f.name}
                  onClick={() => onSelectFolder(f.name)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FolderRow({ folder, label, Icon, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-6 py-1.5 text-sm transition-colors text-left
        ${active
          ? "bg-sky-100 dark:bg-sky-900/30 text-sky-900 dark:text-sky-200 font-semibold"
          : "text-slate-700 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-900"}`}
      data-testid={`email-folder-${folder}`}
    >
      <Icon className="h-3.5 w-3.5 flex-shrink-0"/>
      <span className="truncate">{label}</span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Single row in the message list
// ─────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────
// ViewModeSwitcher · v2.7.45+E13 · 3-state segmented control in the
// page header. Each click cycles to the next mode (3-pane → list →
// compact → 3-pane). Persisted via useEmailViewMode (localStorage +
// DB sync).
// ─────────────────────────────────────────────────────────────────────
function ViewModeSwitcher({ mode, onChange }) {
  const modes = [
    { id: "three-pane", label: "3-Pane",  Icon: Layout,                title: "Three-Pane View (accounts · list · reading)" },
    { id: "list",       label: "List",    Icon: Rows3,                 title: "List View (full-width list, reading in modal)" },
    { id: "compact",    label: "Compact", Icon: SquareSplitVertical,   title: "Compact View (compressed list on top, reading on bottom)" },
  ];
  return (
    <div className="hidden md:inline-flex items-center border border-slate-300 dark:border-zinc-700 rounded-md overflow-hidden h-9" data-testid="email-view-mode-switcher">
      {modes.map((m) => {
        const active = mode === m.id;
        return (
          <button
            key={m.id}
            type="button"
            onClick={() => onChange(m.id)}
            className={`flex items-center gap-1 px-2.5 text-[11px] uppercase tracking-wider font-bold transition-colors h-full
              ${active
                ? "bg-sky-600 text-white"
                : "bg-white dark:bg-zinc-900 text-slate-600 dark:text-zinc-400 hover:bg-slate-100 dark:hover:bg-zinc-800"}`}
            title={m.title}
            data-testid={`email-view-mode-${m.id}`}
          >
            <m.Icon className="h-3.5 w-3.5"/>
            <span className="hidden lg:inline">{m.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function MessageRow({ msg, selected, checked, onToggleCheck, onClick }) {
  const from = (msg.from || "").replace(/<.*?>/g, "").trim() || msg.from || "(unknown)";
  return (
    <div
      className={`w-full border-b border-slate-200 dark:border-zinc-800 transition-colors flex items-stretch
        ${selected
          ? "bg-sky-50 dark:bg-sky-900/20"
          : "hover:bg-slate-50 dark:hover:bg-zinc-900"}`}
      data-testid={`email-row-${msg.uid}`}
    >
      <label
        className="flex items-center px-3 cursor-pointer flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={!!checked}
          onChange={onToggleCheck}
          className="h-4 w-4 rounded border-slate-300 dark:border-zinc-700 text-sky-600 focus:ring-sky-500 cursor-pointer"
          data-testid={`email-row-check-${msg.uid}`}
          aria-label={`Select message: ${msg.subject || "no subject"}`}
        />
      </label>
      <button
        type="button"
        onClick={onClick}
        className="flex-1 min-w-0 text-left p-3 pl-1"
        data-testid={`email-row-open-${msg.uid}`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className={`text-sm truncate ${msg.unread ? "font-bold text-slate-900 dark:text-zinc-50" : "text-slate-700 dark:text-zinc-300"}`}>{from}</div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {/\\Flagged/.test(msg.flags || "") && <Flag className="h-3 w-3 text-amber-500" aria-label="Flagged"/>}
            {/\$Important/.test(msg.flags || "") && <Zap className="h-3 w-3 text-red-500" aria-label="Important"/>}
            <div className="text-[10px] text-slate-500 dark:text-zinc-500">{shortDate(msg.date)}</div>
          </div>
        </div>
        <div className={`text-sm truncate mt-0.5 ${msg.unread ? "font-semibold text-slate-900 dark:text-zinc-100" : "text-slate-600 dark:text-zinc-400"}`}>
          {msg.subject}
        </div>
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ActionToolbar · v2.7.45+E12 · sticky toolbar above the message list
// when ≥1 row is checked. 5 operations: Flag · Important · Archive ·
// Spam · Delete. Each calls back into the parent's run*Action.
// ─────────────────────────────────────────────────────────────────────
function ActionToolbar({ count, running, onClear, onFlag, onImportant, onArchive, onSpam, onDelete }) {
  return (
    <div className="px-3 py-2 border-t border-slate-200 dark:border-zinc-800 bg-sky-50/60 dark:bg-sky-900/15 flex items-center gap-2 flex-wrap" data-testid="email-action-toolbar">
      <div className="text-[11px] uppercase tracking-widest font-bold text-sky-800 dark:text-sky-200 flex-shrink-0">
        {count} selected
      </div>
      <div className="flex items-center gap-1 ml-auto">
        <Button size="sm" variant="outline" disabled={running} onClick={onFlag}
          className="h-7 gap-1 text-xs" title="Toggle flag (\\Flagged)" data-testid="email-action-flag">
          <Flag className="h-3.5 w-3.5 text-amber-500"/>Flag
        </Button>
        <Button size="sm" variant="outline" disabled={running} onClick={onImportant}
          className="h-7 gap-1 text-xs border-red-300 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20"
          title="Mark Important — fires supervisor escalation" data-testid="email-action-important">
          <Zap className="h-3.5 w-3.5 text-red-600 dark:text-red-400"/>Important
        </Button>
        <Button size="sm" variant="outline" disabled={running} onClick={onArchive}
          className="h-7 gap-1 text-xs" title="Move to Archive" data-testid="email-action-archive">
          <Archive className="h-3.5 w-3.5"/>Archive
        </Button>
        <Button size="sm" variant="outline" disabled={running} onClick={onSpam}
          className="h-7 gap-1 text-xs" title="Move to Spam" data-testid="email-action-spam">
          <AlertOctagon className="h-3.5 w-3.5 text-orange-600"/>Spam
        </Button>
        <Button size="sm" variant="outline" disabled={running} onClick={onDelete}
          className="h-7 gap-1 text-xs border-rose-300 dark:border-rose-800 hover:bg-rose-50 dark:hover:bg-rose-900/20"
          title="Move to Trash" data-testid="email-action-delete">
          <Trash2 className="h-3.5 w-3.5 text-rose-600"/>Delete
        </Button>
        <Button size="sm" variant="ghost" disabled={running} onClick={onClear}
          className="h-7 w-7 p-0" title="Clear selection" data-testid="email-action-clear">
          <X className="h-3.5 w-3.5"/>
        </Button>
        {running && <Loader2 className="h-3.5 w-3.5 animate-spin text-sky-600"/>}
      </div>
    </div>
  );
}

function shortDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr.slice(0, 16);
  const now = new Date();
  const same = d.toDateString() === now.toDateString();
  if (same) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

// ─────────────────────────────────────────────────────────────────────
// Email body sanitisation helpers (v2.7.55+E31)
//
//  ▸ stripExternalImages → blocks remote <img>, <video>, <audio>, picture
//    sources and url(...) references in inline style attrs. Inline cid:
//    and data: assets stay untouched. Returns { html, blockedCount }.
//  ▸ htmlToCleanText    → converts HTML to a beautifully formatted
//    text-only article view (block-level newlines, list bullets,
//    heading prefixes). Used by the "View Clean Text" toggle.
// ─────────────────────────────────────────────────────────────────────
function isExternalUrl(src) {
  if (!src) return false;
  const s = src.trim().toLowerCase();
  if (s.startsWith("cid:") || s.startsWith("data:")) return false;
  return /^(https?:)?\/\//i.test(s) || s.startsWith("//");
}

function stripExternalImages(rawHtml) {
  if (!rawHtml) return { html: "", blockedCount: 0 };
  if (typeof DOMParser === "undefined") return { html: rawHtml, blockedCount: 0 };

  const doc = new DOMParser().parseFromString(rawHtml, "text/html");
  let blocked = 0;

  // <img>
  doc.querySelectorAll("img").forEach((el) => {
    const src = el.getAttribute("src") || "";
    const srcset = el.getAttribute("srcset") || "";
    if (isExternalUrl(src) || /^(https?:)?\/\//i.test(srcset.split(",")[0]?.trim() || "")) {
      blocked++;
      el.setAttribute("data-blocked-src", src);
      el.removeAttribute("src");
      el.removeAttribute("srcset");
      el.style.display = "inline-block";
      el.style.minWidth = "0";
      el.style.padding = "4px 8px";
      el.style.background = "rgba(148, 163, 184, 0.15)";
      el.style.border = "1px dashed rgba(148, 163, 184, 0.5)";
      el.style.borderRadius = "4px";
      el.style.color = "rgba(100, 116, 139, 0.9)";
      el.style.fontSize = "11px";
      el.setAttribute("alt", el.getAttribute("alt") || "image blocked");
    }
  });

  // <video>, <audio>, <source>, <iframe>, <picture>
  doc.querySelectorAll("video[src], audio[src], source[src], iframe[src]").forEach((el) => {
    const src = el.getAttribute("src") || "";
    if (isExternalUrl(src)) {
      blocked++;
      el.setAttribute("data-blocked-src", src);
      el.removeAttribute("src");
    }
  });

  // url(...) in inline style attrs (background images, etc.)
  doc.querySelectorAll("[style]").forEach((el) => {
    const style = el.getAttribute("style") || "";
    const cleaned = style.replace(/url\(\s*['"]?(https?:|\/\/)[^)]+\)/gi, () => {
      blocked++;
      return "url()";
    });
    if (cleaned !== style) el.setAttribute("style", cleaned);
  });

  return { html: doc.body.innerHTML, blockedCount: blocked };
}

function htmlToCleanText(rawHtml) {
  if (!rawHtml) return "";
  if (typeof DOMParser === "undefined") return rawHtml;

  const doc = new DOMParser().parseFromString(rawHtml, "text/html");

  // Drop non-readable nodes entirely
  doc.querySelectorAll("script, style, head, meta, link, noscript").forEach((el) => el.remove());

  const out = [];
  const walk = (node, listDepth = 0, listType = null, listCounter = { n: 0 }) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const t = (node.textContent || "").replace(/\s+/g, " ");
      if (t.trim()) out.push(t);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const tag = node.tagName.toLowerCase();
    const blockTags = new Set(["p", "div", "section", "article", "header", "footer", "blockquote", "table", "tr", "ul", "ol", "h1", "h2", "h3", "h4", "h5", "h6", "pre", "br", "hr"]);

    // Emit heading prefix
    if (/^h[1-6]$/.test(tag)) {
      const level = Number(tag.slice(1));
      out.push("\n\n" + "#".repeat(level) + " ");
      [...node.childNodes].forEach((c) => walk(c, listDepth, listType, listCounter));
      out.push("\n");
      return;
    }
    if (tag === "li") {
      const indent = "  ".repeat(Math.max(0, listDepth - 1));
      if (listType === "ol") {
        listCounter.n += 1;
        out.push(`\n${indent}${listCounter.n}. `);
      } else {
        out.push(`\n${indent}• `);
      }
      [...node.childNodes].forEach((c) => walk(c, listDepth, listType, listCounter));
      return;
    }
    if (tag === "ul" || tag === "ol") {
      const counter = { n: 0 };
      [...node.childNodes].forEach((c) => walk(c, listDepth + 1, tag, counter));
      out.push("\n");
      return;
    }
    if (tag === "br" || tag === "hr") {
      out.push(tag === "hr" ? "\n\n---\n\n" : "\n");
      return;
    }
    if (tag === "a") {
      const href = node.getAttribute("href") || "";
      const text = (node.textContent || "").trim();
      if (href && href !== text && /^https?:/i.test(href)) {
        out.push(`${text} (${href})`);
      } else {
        out.push(text);
      }
      return;
    }
    if (tag === "strong" || tag === "b") {
      out.push("**");
      [...node.childNodes].forEach((c) => walk(c, listDepth, listType, listCounter));
      out.push("**");
      return;
    }
    if (tag === "em" || tag === "i") {
      out.push("_");
      [...node.childNodes].forEach((c) => walk(c, listDepth, listType, listCounter));
      out.push("_");
      return;
    }

    [...node.childNodes].forEach((c) => walk(c, listDepth, listType, listCounter));

    if (blockTags.has(tag)) out.push("\n\n");
  };

  walk(doc.body);
  // Collapse 3+ consecutive blank lines, trim outer whitespace
  return out.join("").replace(/\n{3,}/g, "\n\n").trim();
}

// ─────────────────────────────────────────────────────────────────────
// Reading pane
// ─────────────────────────────────────────────────────────────────────
function ReadingPane({ msg, account, onReply, onForward, onImportant }) {
  // v2.7.45+E7 · Translation overlay — when active, we render the
  // translated body instead of the original; "View Original" wipes the
  // override.
  const [translated, setTranslated] = useState(null);
  const [escalating, setEscalating] = useState(false);
  // v2.7.55+E31 · Readability + image-shield state
  const [showImages, setShowImages] = useState(false);       // off by default per security spec
  const [viewMode, setViewMode] = useState("html");          // "html" | "clean"
  const showHtml = !!msg.html;

  // Re-derive source HTML when translation/message changes, then run
  // through the image shield. blockedCount drives the banner visibility.
  const sourceHtml = translated || msg.html || "";
  const { html: shieldedHtml, blockedCount } = useMemo(
    () => stripExternalImages(sourceHtml),
    [sourceHtml],
  );
  const cleanText = useMemo(
    () => (sourceHtml ? htmlToCleanText(sourceHtml) : (translated || msg.text || "")),
    [sourceHtml, translated, msg.text],
  );

  // Reset view state when the message changes
  useEffect(() => {
    setShowImages(false);
    setViewMode("html");
    setTranslated(null);
  }, [msg?.id, msg?.uid]);

  const handleImportant = async () => {
    if (escalating) return;
    setEscalating(true);
    try { await onImportant(); } finally { setEscalating(false); }
  };
  return (
    <div className="p-5">
      <div className="border-b border-slate-200 dark:border-zinc-800 pb-3 mb-4">
        <h2 className="text-xl font-bold text-slate-900 dark:text-zinc-50 mb-2" data-testid="email-reading-subject">{msg.subject || "(no subject)"}</h2>
        <div className="text-sm text-slate-700 dark:text-zinc-300"><b>From:</b> {msg.from}</div>
        <div className="text-sm text-slate-600 dark:text-zinc-400"><b>To:</b> {msg.to}</div>
        {msg.cc && <div className="text-sm text-slate-600 dark:text-zinc-400"><b>Cc:</b> {msg.cc}</div>}
        <div className="text-xs text-slate-500 dark:text-zinc-500 mt-1">{msg.date}</div>
        <div className="mt-3 flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={onReply} className="gap-1.5" data-testid="email-reply-btn">
            <Reply className="h-3.5 w-3.5"/>Reply
          </Button>
          <Button size="sm" variant="outline" onClick={onForward} className="gap-1.5" data-testid="email-forward-btn">
            <Forward className="h-3.5 w-3.5"/>Forward
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleImportant}
            disabled={escalating}
            className="gap-1.5 border-red-300 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20"
            title="Mark Important — instantly escalates to supervisors"
            data-testid="email-reading-important-btn"
          >
            {escalating
              ? <Loader2 className="h-3.5 w-3.5 animate-spin"/>
              : <Zap className="h-3.5 w-3.5 text-red-600 dark:text-red-400"/>}
            Mark Important
          </Button>
        </div>
        <div className="mt-3 pt-2 border-t border-slate-200 dark:border-zinc-800/50 flex items-center justify-between gap-3 flex-wrap">
          <TranslationToggle
            original={showHtml ? msg.html : (msg.text || "")}
            isHtml={showHtml}
            onTranslated={setTranslated}
          />
          {/* v2.7.55+E31 · Readability toggle (top-right of the meta strip). */}
          {showHtml && (
            <div className="inline-flex rounded border border-slate-300 dark:border-zinc-700 text-xs overflow-hidden flex-shrink-0" data-testid="email-view-toggle">
              <button
                type="button"
                onClick={() => setViewMode("html")}
                className={`px-2.5 py-1 transition-colors ${viewMode === "html" ? "bg-sky-500/15 text-sky-700 dark:text-sky-300 font-bold" : "text-slate-600 dark:text-zinc-400 hover:bg-slate-100 dark:hover:bg-zinc-800"}`}
                data-testid="email-view-toggle-html"
                aria-pressed={viewMode === "html"}
              >
                View Raw HTML
              </button>
              <button
                type="button"
                onClick={() => setViewMode("clean")}
                className={`px-2.5 py-1 border-l border-slate-300 dark:border-zinc-700 transition-colors ${viewMode === "clean" ? "bg-sky-500/15 text-sky-700 dark:text-sky-300 font-bold" : "text-slate-600 dark:text-zinc-400 hover:bg-slate-100 dark:hover:bg-zinc-800"}`}
                data-testid="email-view-toggle-clean"
                aria-pressed={viewMode === "clean"}
              >
                View Clean Text Only
              </button>
            </div>
          )}
        </div>
      </div>

      {/* v2.7.55+E31 · "See Images" banner — only renders when external
          images were blocked AND the user hasn't already opted in. */}
      {showHtml && viewMode === "html" && blockedCount > 0 && !showImages && (
        <div
          className="mb-4 flex items-center justify-between gap-3 rounded-md border border-amber-300/70 dark:border-amber-700/40 bg-amber-50 dark:bg-amber-900/15 px-3 py-2.5 text-xs"
          data-testid="email-images-blocked-banner"
        >
          <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
            <ImageIcon className="h-4 w-4 flex-shrink-0"/>
            <span>
              <b>{blockedCount}</b> external image{blockedCount === 1 ? "" : "s"} blocked for your privacy and bandwidth.
            </span>
          </div>
          <Button
            size="sm"
            onClick={() => setShowImages(true)}
            className="h-7 gap-1.5 bg-amber-500 hover:bg-amber-400 text-zinc-950 text-xs font-bold"
            data-testid="email-show-images-btn"
          >
            <ImageIcon className="h-3.5 w-3.5"/>See Images
          </Button>
        </div>
      )}

      {/* Body */}
      {showHtml && viewMode === "html" ? (
        <div
          className="prose prose-sm dark:prose-invert max-w-3xl leading-relaxed text-slate-800 dark:text-zinc-200 prose-headings:text-slate-900 dark:prose-headings:text-zinc-50 prose-a:text-sky-600 dark:prose-a:text-sky-400 prose-img:rounded prose-img:my-2 prose-table:text-sm prose-table:my-3 prose-pre:bg-slate-100 dark:prose-pre:bg-zinc-900 prose-pre:text-xs prose-pre:whitespace-pre-wrap break-words"
          dangerouslySetInnerHTML={{ __html: showImages ? sourceHtml : shieldedHtml }}
          data-testid="email-html-body"
        />
      ) : showHtml && viewMode === "clean" ? (
        <pre
          className="whitespace-pre-wrap font-sans text-[15px] leading-relaxed text-slate-800 dark:text-zinc-200 max-w-3xl break-words"
          data-testid="email-clean-text-body"
        >
          {cleanText || "(empty body)"}
        </pre>
      ) : (
        <pre
          className="whitespace-pre-wrap font-sans text-[15px] leading-relaxed text-slate-800 dark:text-zinc-200 max-w-3xl break-words"
          data-testid="email-text-body"
        >
          {translated || msg.text || "(empty body)"}
        </pre>
      )}
      {msg.attachments && msg.attachments.length > 0 && (
        <div className="mt-6 pt-4 border-t border-slate-200 dark:border-zinc-800">
          <div className="text-xs uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400 mb-2">Attachments ({msg.attachments.length})</div>
          <div className="flex flex-wrap gap-2">
            {msg.attachments.map((a, i) => (
              <div key={`${a.filename || "att"}-${i}`} className="px-3 py-2 border border-slate-200 dark:border-zinc-700 rounded text-xs bg-slate-50 dark:bg-zinc-900">
                <div className="font-bold">{a.filename || "(no name)"}</div>
                <div className="text-slate-500 dark:text-zinc-500">{a.content_type} · {formatBytes(a.size)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function formatBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// ─────────────────────────────────────────────────────────────────────
// Composer modal
// ─────────────────────────────────────────────────────────────────────
function ComposerModal({ accounts, defaultAccount, seed, onClose }) {
  const [fromId, setFromId] = useState(defaultAccount?.id || accounts[0]?.id || "");
  const [to, setTo] = useState(seed?.from && seed.mode === "reply" ? cleanAddr(seed.from) : "");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState(seed
    ? (seed.mode === "reply"   ? "Re: "  + (seed.subject || "")
     : seed.mode === "forward" ? "Fwd: " + (seed.subject || "")
     : "")
    : "");
  const [body, setBody] = useState(seed
    ? `\n\n----- Original Message -----\nFrom: ${seed.from}\nDate: ${seed.date}\nSubject: ${seed.subject}\n\n${seed.text || stripHtml(seed.html || "")}`
    : "");
  const [sending, setSending] = useState(false);

  // v2.7.65+E52 · Phase 2 · File attachments (10MB total cap).
  const ATTACH_MAX_BYTES = 10 * 1024 * 1024;
  const [attachments, setAttachments] = useState([]);   // File[]
  const attachedSize = useMemo(
    () => attachments.reduce((sum, f) => sum + (f.size || 0), 0),
    [attachments],
  );

  // v2.7.65+E52.1 · Recent Attachments gallery — populated from the
  // outbound sent-log. De-duped by fileUrl so a file re-attached across
  // multiple sends only shows up once, and capped at 8 chips.
  const [recentAttachments, setRecentAttachments] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [reattachingUrl, setReattachingUrl] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRecentLoading(true);
      try {
        const { data } = await adminApi.get("/admin/email/sent-log?limit=50");
        if (cancelled) return;
        const seen = new Set();
        const flat = [];
        for (const row of data?.items || []) {
          for (const att of row.attachments || []) {
            if (!att.fileUrl || seen.has(att.fileUrl)) continue;
            seen.add(att.fileUrl);
            flat.push({ ...att, sent_at: row.sent_at, subject: row.subject });
            if (flat.length >= 8) break;
          }
          if (flat.length >= 8) break;
        }
        setRecentAttachments(flat);
      } catch { /* silent — history is optional */ }
      finally { if (!cancelled) setRecentLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const onReattachRecent = async (att) => {
    // Fetch the file bytes from GridFS and inject them into the picker
    // list. Respects the same 10MB combined cap as regular file picks.
    const projected = attachedSize + (att.fileSize || 0);
    if (projected > ATTACH_MAX_BYTES) {
      toast.error(`Adding "${att.fileName}" would exceed the 10 MB limit`);
      return;
    }
    setReattachingUrl(att.fileUrl);
    try {
      const { data: blob } = await adminApi.get(att.fileUrl, { responseType: "blob" });
      const file = new File([blob], att.fileName || "attachment.bin",
        { type: blob.type || "application/octet-stream" });
      setAttachments((arr) => [...arr, file]);
      toast.success(`Re-attached "${att.fileName}"`);
    } catch (e) {
      toast.error(`Re-attach failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setReattachingUrl("");
    }
  };

  // v2.7.45+E6 · Template library
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [savingAsTemplate, setSavingAsTemplate] = useState(false);

  // v2.7.45+E7 · Signature library + per-mailbox default mapping
  const [signatures, setSignatures] = useState([]);
  const [mailboxSigMap, setMailboxSigMap] = useState({});
  const [selectedSigId, setSelectedSigId] = useState("");
  // Active signature HTML drives the preview block (purely client-side
  // — no roundtrip when the user flips From or Signature).
  const activeSignature = useMemo(
    () => signatures.find((s) => s.id === selectedSigId) || null,
    [signatures, selectedSigId],
  );

  useEffect(() => {
    let cancelled = false;
    adminApi.get("/admin/email/templates")
      .then((r) => { if (!cancelled) setTemplates(r.data?.items || []); })
      .catch(() => { /* silent — templates are optional */ });
    return () => { cancelled = true; };
  }, []);

  // Load signatures + per-mailbox mapping on mount; reuse the cached
  // signatures array for both the dropdown and the From-swap logic.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/email/signatures");
        if (cancelled) return;
        const items = data?.items || [];
        setSignatures(items);
        // Per-mailbox default-signature mapping
        const entries = await Promise.all((accounts || []).map(async (a) => {
          try {
            const { data: m } = await adminApi.get(`/admin/email/accounts/${a.id}/signature`);
            return [a.id, m?.default_signature_id || ""];
          } catch { return [a.id, ""]; }
        }));
        if (cancelled) return;
        const map = Object.fromEntries(entries);
        setMailboxSigMap(map);
        // Pick the From's default signature (or personal default, or first)
        const fromDefault = map[fromId];
        if (fromDefault) {
          setSelectedSigId(fromDefault);
        } else {
          const personalDefault = items.find((s) => s.is_default_personal);
          if (personalDefault) setSelectedSigId(personalDefault.id);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [accounts]);

  // When the user picks a different From account, auto-swap the
  // signature to that mailbox's default (spec §4.2). Pure client-side
  // — no backend hit.
  useEffect(() => {
    const mapped = mailboxSigMap[fromId];
    if (mapped) setSelectedSigId(mapped);
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [fromId]);

  const insertTemplate = (templateId) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;
    const t = templates.find((x) => x.id === templateId);
    if (!t) return;
    // Only stomp existing subject if it's empty / forwarded blank
    const subjectIsEmpty = !subject || subject === "Re: " || subject === "Fwd: ";
    if (subjectIsEmpty) setSubject(t.subject || "");
    // Body: prepend the template, keep any reply-quote tail
    setBody((current) => {
      if (!current.trim()) return t.body || "";
      // If the current body is just a forwarded/replied quote, prepend template
      const isQuote = current.trimStart().startsWith("----- Original Message -----");
      if (isQuote) return `${t.body || ""}\n${current}`;
      return current;  // user has custom content; don't clobber
    });
  };

  const saveAsTemplate = async () => {
    const name = window.prompt("Save this draft as a template — name?");
    if (!name || !name.trim()) return;
    const shared = window.confirm("Share this template with the whole admin team?\n\n• OK = shared with everyone\n• Cancel = personal (only you can see it)");
    setSavingAsTemplate(true);
    try {
      await adminApi.post("/admin/email/templates", { name: name.trim(), subject, body, shared });
      toast.success(`Template "${name.trim()}" saved`);
      // Refresh the list so the new template appears in the dropdown
      const r = await adminApi.get("/admin/email/templates");
      setTemplates(r.data?.items || []);
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSavingAsTemplate(false);
    }
  };

  const onFilesPicked = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;
    // Enforce combined-size cap against BOTH new picks and existing set.
    const combined = [...attachments, ...picked];
    const total = combined.reduce((s, f) => s + (f.size || 0), 0);
    if (total > ATTACH_MAX_BYTES) {
      const mb = (total / 1024 / 1024).toFixed(1);
      toast.error(`Attachments exceed the 10 MB limit (${mb} MB selected)`);
      // Reset the input so the same file can be re-picked after user
      // removes some existing attachments.
      if (e.target) e.target.value = "";
      return;
    }
    setAttachments(combined);
    if (e.target) e.target.value = "";
  };

  const onRemoveAttachment = (idx) => {
    setAttachments((arr) => arr.filter((_, i) => i !== idx));
  };

  const handleSend = async () => {
    if (!fromId) return toast.error("Pick a sending account");
    if (!to.trim()) return toast.error("Add at least one recipient");
    if (attachedSize > ATTACH_MAX_BYTES) {
      return toast.error(`Attachments exceed the 10 MB limit`);
    }
    setSending(true);
    try {
      const toList = to.split(",").map((s) => s.trim()).filter(Boolean);
      const ccList = cc.split(",").map((s) => s.trim()).filter(Boolean);
      // v2.7.45+E7 · Append the active signature to both text + html parts.
      // For text/plain we just append a basic textual signature derived
      // from the layout; for text/html we attach the compiled HTML.
      let outBody = body;
      let outHtml = null;
      if (activeSignature) {
        const sigText = signatureToPlain(activeSignature.layout_json);
        if (sigText) outBody = `${body}\n\n${sigText}`;
        outHtml = `${body.replace(/\n/g, "<br/>")}<br/><br/>${activeSignature.html || ""}`;
      }
      if (attachments.length > 0) {
        // Multipart path — attachments require the new endpoint.
        const fd = new FormData();
        fd.append("to", toList.join(","));
        if (ccList.length) fd.append("cc", ccList.join(","));
        fd.append("subject", subject);
        fd.append("text", outBody);
        if (outHtml) fd.append("html", outHtml);
        attachments.forEach((f) => fd.append("files", f, f.name));
        await adminApi.post(
          `/admin/email/accounts/${fromId}/send-with-attachments`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
      } else {
        await adminApi.post(`/admin/email/accounts/${fromId}/send`, {
          to: toList, cc: ccList, bcc: [],
          subject, text: outBody, html: outHtml,
        });
      }
      toast.success(
        attachments.length > 0
          ? `Message sent with ${attachments.length} attachment${attachments.length > 1 ? "s" : ""}`
          : "Message sent",
      );
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="email-composer-backdrop">
      <div className="bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zinc-800">
          <div className="font-bold text-lg flex items-center gap-2"><MailPlus className="h-5 w-5 text-sky-500"/>New Email</div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="email-composer-close"><X className="h-4 w-4"/></Button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-zinc-400">From</label>
              <Select value={fromId} onValueChange={setFromId}>
                <SelectTrigger data-testid="email-composer-from"><SelectValue placeholder="Pick a mailbox"/></SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-zinc-400 flex items-center justify-between">
                <span>Template</span>
                {templates.length > 0 && (
                  <span className="text-[10px] font-normal text-slate-500 dark:text-zinc-500 normal-case tracking-normal">{templates.length} available</span>
                )}
              </label>
              <Select value={selectedTemplateId} onValueChange={insertTemplate}>
                <SelectTrigger data-testid="email-composer-template"><SelectValue placeholder="Insert template…"/></SelectTrigger>
                <SelectContent className="max-h-64">
                  {templates.length === 0 && (
                    <div className="px-3 py-2 text-xs text-slate-500 dark:text-zinc-500">No templates yet</div>
                  )}
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                      {t.is_system && <span className="ml-2 text-[9px] uppercase tracking-widest text-sky-500">system</span>}
                      {!t.is_system && t.shared && <span className="ml-2 text-[9px] uppercase tracking-widest text-emerald-500">team</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-zinc-400 flex items-center justify-between">
                <span>Signature</span>
                {signatures.length > 0 && (
                  <span className="text-[10px] font-normal text-slate-500 dark:text-zinc-500 normal-case tracking-normal">{signatures.length} available</span>
                )}
              </label>
              <Select value={selectedSigId || "none"} onValueChange={(v) => setSelectedSigId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="email-composer-signature"><SelectValue placeholder="No signature"/></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="none">— None —</SelectItem>
                  {signatures.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                      {s.is_system && <span className="ml-2 text-[9px] uppercase tracking-widest text-sky-500">system</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-zinc-400">To</label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Comma-separated emails" data-testid="email-composer-to"/>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-zinc-400">Cc</label>
            <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="Comma-separated emails (optional)" data-testid="email-composer-cc"/>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-zinc-400">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" data-testid="email-composer-subject"/>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-zinc-400">Message</label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} placeholder="Write your message…" data-testid="email-composer-body"/>
          </div>
          {activeSignature && (
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-zinc-400 flex items-center justify-between">
                <span>Signature Preview</span>
                <span className="text-[10px] font-normal text-slate-500 dark:text-zinc-500 normal-case tracking-normal">{activeSignature.name}</span>
              </label>
              <div
                className="bg-white p-3 rounded border border-slate-200 max-h-56 overflow-y-auto"
                data-testid="email-composer-signature-preview"
                dangerouslySetInnerHTML={{ __html: activeSignature.html || "" }}
              />
            </div>
          )}

          {/* v2.7.65+E52 · Phase 2 · Attachments block */}
          <div className="space-y-1.5" data-testid="email-composer-attachments">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-zinc-400 flex items-center justify-between">
              <span>Attachments</span>
              <span
                className={`text-[10px] font-mono ${attachedSize > ATTACH_MAX_BYTES ? "text-rose-500" : "text-slate-500 dark:text-zinc-500"}`}
                data-testid="email-composer-attach-size"
              >
                {formatBytes(attachedSize)} / 10.0 MB
              </span>
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                id="composer-file-input"
                type="file"
                multiple
                className="hidden"
                onChange={onFilesPicked}
                data-testid="email-composer-file-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => document.getElementById("composer-file-input")?.click()}
                disabled={sending}
                data-testid="email-composer-attach-btn"
                title="Attach files (10 MB combined maximum)"
              >
                <Paperclip className="h-3.5 w-3.5"/>
                Attach files
              </Button>
              {attachments.length === 0 && (
                <span className="text-[11px] text-slate-500 dark:text-zinc-500">
                  Combined size must stay ≤ 10 MB.
                </span>
              )}
              {attachments.map((f, i) => (
                <div
                  key={`${f.name}-${i}`}
                  className="flex items-center gap-1.5 px-2 py-1 rounded bg-slate-100 dark:bg-zinc-800 border border-slate-200 dark:border-zinc-700 text-[11px]"
                  data-testid={`email-composer-attach-chip-${i}`}
                >
                  <Paperclip className="h-3 w-3 text-sky-600"/>
                  <span className="max-w-[180px] truncate font-medium">{f.name}</span>
                  <span className="text-slate-500 dark:text-zinc-500 font-mono">{formatBytes(f.size)}</span>
                  <button
                    type="button"
                    onClick={() => onRemoveAttachment(i)}
                    disabled={sending}
                    className="ml-1 text-rose-500 hover:text-rose-700 disabled:opacity-40"
                    data-testid={`email-composer-attach-remove-${i}`}
                    title="Remove"
                    aria-label={`Remove ${f.name}`}
                  >
                    <X className="h-3 w-3"/>
                  </button>
                </div>
              ))}
            </div>
            {attachedSize > ATTACH_MAX_BYTES && (
              <div
                className="text-[11px] text-rose-500 font-semibold"
                data-testid="email-composer-attach-oversize"
              >
                Combined attachments exceed 10 MB — remove one or more to send.
              </div>
            )}

            {/* v2.7.65+E52.1 · Recent Attachments — one-click re-attach.
                Pulls from the sent-log audit collection built in Phase 2. */}
            {(recentAttachments.length > 0 || recentLoading) && (
              <div className="mt-3 pt-3 border-t border-slate-200 dark:border-zinc-800" data-testid="email-composer-recent-attachments">
                <div className="flex items-center gap-1.5 mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-zinc-500">
                  <History className="h-3 w-3"/>
                  Recent attachments
                  {recentLoading && <Loader2 className="h-3 w-3 animate-spin"/>}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {recentAttachments.map((att) => {
                    const busy = reattachingUrl === att.fileUrl;
                    return (
                      <button
                        key={att.fileUrl}
                        type="button"
                        onClick={() => onReattachRecent(att)}
                        disabled={busy || sending}
                        className="flex items-center gap-1.5 px-2 py-1 rounded border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/20 text-[11px] hover:bg-sky-100 dark:hover:bg-sky-900/40 transition disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={`email-composer-recent-attach-${att.fileUrl.split("/").pop()}`}
                        title={`Re-attach · ${att.fileName} (${formatBytes(att.fileSize)}) · from "${att.subject || "(no subject)"}"`}
                      >
                        {busy
                          ? <Loader2 className="h-3 w-3 animate-spin text-sky-600"/>
                          : <Paperclip className="h-3 w-3 text-sky-600"/>}
                        <span className="max-w-[140px] truncate font-medium text-sky-900 dark:text-sky-200">{att.fileName}</span>
                        <span className="text-sky-700/60 dark:text-sky-400/60 font-mono">{formatBytes(att.fileSize)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 dark:border-zinc-800 flex items-center justify-between">
          <div className="text-xs text-slate-500 dark:text-zinc-500">SMTP via IONOS · TLS</div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={saveAsTemplate}
              disabled={savingAsTemplate || (!body.trim() && !subject.trim())}
              data-testid="email-composer-save-template"
              title="Save the current subject + body as a reusable template"
              className="gap-1.5"
            >
              {savingAsTemplate ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <FileText className="h-3.5 w-3.5"/>}
              Save as template
            </Button>
            <Button variant="outline" onClick={onClose} data-testid="email-composer-cancel">Cancel</Button>
            <Button onClick={handleSend} disabled={sending || attachedSize > ATTACH_MAX_BYTES} className="bg-sky-600 hover:bg-sky-500 text-white font-bold gap-2 disabled:opacity-50" data-testid="email-composer-send">
              {sending ? <Loader2 className="h-4 w-4 animate-spin"/> : <Send className="h-4 w-4"/>}
              {sending ? "Sending…" : "Send"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function cleanAddr(s) {
  if (!s) return "";
  const m = s.match(/<([^>]+)>/);
  return m ? m[1] : s.trim();
}

function stripHtml(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

// v2.7.45+E7 · Plain-text fallback for the signature block — used in
// the text/plain MIME part so clients that block HTML still see the
// sender's contact info.
function signatureToPlain(layout) {
  if (!layout) return "";
  const lines = [];
  if (layout.name)       lines.push(layout.name);
  const titleParts = [layout.job_title, layout.department, layout.company].filter(Boolean);
  if (titleParts.length) lines.push(titleParts.join(" | "));
  if (layout.email)        lines.push(`E: ${layout.email}`);
  if (layout.direct_phone) lines.push(`D: ${layout.direct_phone}`);
  if (layout.mobile_phone) lines.push(`M: ${layout.mobile_phone}`);
  if (layout.website)      lines.push(`W: ${layout.website}`);
  if (layout.address)      lines.push(`A: ${layout.address}`);
  return lines.join("\n");
}
